import { useState, useEffect, useRef } from "react";
import { monitorService } from "@/services/monitorService";
import type { MonitorEventListItem } from "@/services/monitorService";
import { Shield, Globe, Search, RefreshCw } from "lucide-react";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";

function SiteFavicon({ domain, dm }: { domain: string | null | undefined; dm: boolean }) {
  const [failed, setFailed] = useState(false);
  if (!domain || failed) {
    return <Globe className={`h-4 w-4 shrink-0 ${dm ? 'text-slate-500' : 'text-slate-400'}`} />;
  }
  const cleanDomain = domain.replace(/^https?:\/\//, '').split('/')[0];
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(cleanDomain)}&sz=32`}
      alt=""
      className="h-4 w-4 shrink-0 rounded-sm object-contain"
      onError={() => setFailed(true)}
    />
  );
}

const POLL_INTERVAL = 15_000; // 15s

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function EventCard({ event, dm }: { event: MonitorEventListItem; dm: boolean }) {
  const flagged = event.isFlagged;
  return (
    <div
      className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
        flagged
          ? (dm ? 'border-red-800 bg-red-900/20' : 'border-red-200 bg-red-50')
          : (dm ? 'border-slate-700 bg-slate-800' : 'border-slate-100 bg-white')
      }`}
    >
      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className={`text-sm font-semibold truncate ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{event.studentName}</span>
          <span className={`text-xs shrink-0 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{timeAgo(event.createdAt)}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <SiteFavicon domain={event.domain || event.url} dm={dm} />
          <span className={`text-xs truncate ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{event.domain || event.url || 'Unknown site'}</span>
        </div>
        {event.pageTitle && (
          <p className={`mt-1 text-xs line-clamp-1 ${dm ? 'text-slate-400' : 'text-slate-600'}`}>{event.pageTitle}</p>
        )}
        {flagged && event.flagReason && (
          <span className="mt-1.5 inline-block px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
            {event.flagReason}
          </span>
        )}
      </div>

      {/* Status pill */}
      <div className="shrink-0">
        {flagged
          ? <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-semibold">Flagged</span>
          : <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">Safe</span>
        }
      </div>
    </div>
  );
}

export function LiveActivity() {
  const { dm } = useTeacherTheme();
  const [events, setEvents] = useState<MonitorEventListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'flagged' | 'safe'>('all');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async (showSpinner = false, loadMore = false) => {
    if (showSpinner) setIsRefreshing(true);
    try {
      const currentPage = loadMore ? page : 1;
      const res = await monitorService.getEvents(currentPage, 10);
      if (loadMore) {
        setEvents(prev => [...prev, ...res.data]);
        setPage(p => p + 1);
      } else {
        setEvents(res.data);
        setPage(1);
      }
      setHasMore(res.data.length === 10);
      setLastRefresh(new Date());
      setError(null);
    } catch {
      setError('Could not load activity feed.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(), POLL_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const handleLoadMore = () => {
    load(false, true);
  };

  const filtered = events.filter(e => {
    if (filter === 'flagged' && !e.isFlagged) return false;
    if (filter === 'safe' && e.isFlagged) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        e.studentName.toLowerCase().includes(q) ||
        (e.domain ?? '').toLowerCase().includes(q) ||
        (e.pageTitle ?? '').toLowerCase().includes(q) ||
        (e.flagReason ?? '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const flaggedCount = events.filter(e => e.isFlagged).length;

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className={`text-lg font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Live Activity</h2>
          <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
            Last updated {timeAgo(lastRefresh.toISOString())} · auto-refreshes every 15s
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={isRefreshing}
          className="btn-glass btn-glass-pill flex items-center gap-2"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Combined stats bar */}
      <div className={`flex rounded-xl border shadow-sm overflow-hidden ${dm ? 'border-slate-700' : 'border-slate-200'}`}>
        <div className={`flex-1 px-5 py-3 text-center ${dm ? 'bg-slate-800' : 'bg-white'}`}>
          <p className={`text-2xl font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{events.length}</p>
          <p className={`text-xs mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Total Events</p>
        </div>
        <div className={`w-px ${dm ? 'bg-slate-700' : 'bg-slate-200'}`} />
        <div className={`flex-1 px-5 py-3 text-center ${dm ? 'bg-red-900/20' : 'bg-red-50'}`}>
          <p className="text-2xl font-bold text-red-600">{flaggedCount}</p>
          <p className="text-xs mt-0.5 text-red-500">Flagged</p>
        </div>
        <div className={`w-px ${dm ? 'bg-slate-700' : 'bg-slate-200'}`} />
        <div className={`flex-1 px-5 py-3 text-center ${dm ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
          <p className="text-2xl font-bold text-emerald-600">{events.length - flaggedCount}</p>
          <p className="text-xs mt-0.5 text-emerald-500">Safe</p>
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dm ? 'text-slate-500' : 'text-slate-400'}`} />
          <input
            type="text"
            placeholder="Search student, domain, reason…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-200 ${dm ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500' : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'}`}
          />
        </div>
        <div className="flex gap-1.5" data-dm={dm}>
          {(['all', 'flagged', 'safe'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`btn-glass btn-glass-pill ${
                filter === f
                  ? f === 'flagged' ? 'btn-glass-danger' : f === 'safe' ? 'btn-glass-emerald' : 'btn-glass-blue'
                  : ''
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-400 text-sm">Loading activity…</div>
      ) : error ? (
        <div className="flex items-center justify-center h-48 text-red-400 text-sm">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-slate-400 text-sm gap-2">
          <Shield className="h-8 w-8 text-slate-200" />
          <span>No activity matches your filters</span>
        </div>
      ) : (
        <div className="space-y-2.5">
          {/* Live pulse indicator */}
          <div className="flex items-center gap-2 text-xs text-emerald-600 font-medium">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Live — {filtered.length} event{filtered.length !== 1 ? 's' : ''}
          </div>
          {filtered.map(event => (
            <EventCard key={event.id} event={event} dm={dm} />
          ))}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <button
                onClick={handleLoadMore}
                disabled={isRefreshing}
                className="btn-glass btn-glass-pill flex items-center gap-2"
              >
                {isRefreshing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Load More
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
